import React, { useEffect, useState } from "react";
import { Box, Typography, Button, Modal, Stack, Chip } from "@mui/material";
import { Client } from "api/models/Client";
import { Snackbar } from "types/snackbar";
import PostAddIcon from "@mui/icons-material/PostAdd";
import AddPortfolioContractModal from "components/Modals/AddPortfolioContract";
import { useAppDispatch, useAppSelector } from "hooks";
import { selectPortfolioContracts } from "store/selectors";
import { fetchPortfolioContracts } from "store/appSlice";
import dayjs from "dayjs";
import Table from "components/Table";
import ConfirmationModal from "components/Modals/Confirmation";
import EditPortfolioContractModal from "components/Modals/EditPortfolioContract";
import { deletePortfolioContract } from "api/resources/contracts";

interface PortfolioContractsProps {
  client: Client;
  setSnackbar: React.Dispatch<React.SetStateAction<Snackbar>>;
}

const PortfolioContracts: React.FC<PortfolioContractsProps> = ({
  client,
  setSnackbar,
}: PortfolioContractsProps) => {
  const [addContractModalOpen, setAddContractModalOpen] = useState(false);
  const [editContractModal, setEditContractModal] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);

  const portfolioContracts = useAppSelector(selectPortfolioContracts);
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(fetchPortfolioContracts(client.id));
  }, [client]);

  const columns = [
    {
      accessorKey: "validFrom",
      header: "Valid from",
      cell: (row: any) => {
        return dayjs(row.row.original.validFrom).format("DD.MM.YYYY");
      },
      flex: 1,
    },
    {
      accessorKey: "validTo",
      header: "Valid to",
      cell: (row: any) => {
        return dayjs(row.row.original.validTo).format("DD.MM.YYYY");
      },
      flex: 1,
    },
    {
      header: "Status",
      flex: 1,
      cell: (row: any) => {
        const contract = row.row.original;
        const isActive =
          dayjs(contract.validFrom) <= dayjs() &&
          dayjs(contract.validTo) >= dayjs();
        const inThePast = dayjs(contract.validFrom) < dayjs();
        const inTheFuture = dayjs(contract.validTo) > dayjs();

        if (isActive) {
          return <Chip label="Active" color="success" size="small" />;
        }
        if (inThePast) {
          return <Chip label="Passed" color="default" size="small" />;
        }
        if (inTheFuture) {
          return <Chip label="Upcoming" color="default" size="small" />;
        }
      },
    },
    {
      header: "Actions",
      flex: 1,
      cell: (row: any) => {
        return (
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={() => setEditContractModal(row.row.original)}
            >
              Edit
            </Button>
            <Button
              variant="text"
              color="primary"
              size="small"
              onClick={() => setDeleteModal(row.row.original.id)}
            >
              Delete
            </Button>
          </Stack>
        );
      },
    },
  ];

  if (!portfolioContracts) {
    return null;
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: "2rem",
        }}
      >
        <Typography variant="h5" component="h5">
          Portfolio contracts
        </Typography>
        <Button
          size="small"
          variant="contained"
          startIcon={<PostAddIcon />}
          onClick={() => setAddContractModalOpen(true)}
        >
          Add contract
        </Button>
      </Box>
      <Box>
        {portfolioContracts?.content.length === 0 ? (
          <Typography component="p">No portfolio contracts to show</Typography>
        ) : (
          <Table
            columns={columns}
            data={[...portfolioContracts?.content].sort(
              (a, b) => new Date(b.validFrom) - new Date(a.validFrom),
            )}
          />
        )}
      </Box>
      {addContractModalOpen && (
        <Modal
          open={addContractModalOpen}
          onClose={() => setAddContractModalOpen(false)}
        >
          <Box>
            <AddPortfolioContractModal
              setOpenModal={setAddContractModalOpen}
              setSnackbar={setSnackbar}
              clientId={client.id}
            />
          </Box>
        </Modal>
      )}
      <Modal open={deleteModal} onClose={() => setDeleteModal(null)}>
        <ConfirmationModal
          title={`Do you want to delete this contract?`}
          subtitle="This will delete the contract permanently. This action cannot be undone."
          buttonTitle="Delete"
          action={() => {
            deletePortfolioContract(deleteModal)
              .then(() => {
                dispatch(fetchPortfolioContracts(client.id));
                setDeleteModal(null);
                setSnackbar({
                  open: true,
                  severity: "info",
                  message: "Contract deleted",
                });
              })
              .catch(() => {
                setSnackbar({
                  open: true,
                  severity: "error",
                  message: "Error deleting contract",
                });
              });
          }}
          setConfirmationModal={setDeleteModal}
        />
      </Modal>
      {editContractModal && (
        <Modal
          open={editContractModal}
          onClose={() => setEditContractModal(null)}
        >
          <EditPortfolioContractModal
            clientId={client.id}
            contractId={editContractModal.id}
            currentValue={editContractModal.validTo}
            setOpenModal={setEditContractModal}
            setSnackbar={setSnackbar}
          />
        </Modal>
      )}
    </Box>
  );
};

export default PortfolioContracts;
